<template>
  <v-dialog v-model="showDialog" max-width="700px" @update:model-value="onDialogClose">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ simDetails && simDetails.sim_id ? 'SIM Details' : 'IMSI Details' }}</span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-if="!loading && simDetails">
          <!-- IMSI Info -->
          <v-row>
            <v-col cols="12">
              <h3 class="mb-2">IMSI: {{ simDetails.imsi || '-' }}</h3>
              <span class="detail-label mr-2">Stare subscriber in retea:</span>
              <v-chip :color="getStatusColor(simDetails.sim_status)" size="small" class="mr-2">
                {{ formatStatus(simDetails.sim_status) }}
              </v-chip>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- Basic Details Grid -->
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Serial:</span>
                <span class="detail-value">{{ simDetails.serial || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">MSISDN:</span>
                <span class="detail-value">{{ simDetails.msisdn || '-' }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Client:</span>
                <span class="detail-value">{{ simDetails.client_name || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Subscriber ID:</span>
                <span class="detail-value">{{ simDetails.subscriber_id || '-' }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Start Date:</span>
                <span class="detail-value">{{ formatDate(simDetails.start_date) }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Expiration Date:</span>
                <span class="detail-value" :class="isExpiringSoon(simDetails.expiration_date) ? 'text-red' : ''">
                  {{ formatDate(simDetails.expiration_date) }}
                </span>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- Client Services (current SIM) -->
          <h4 class="mb-2">Client Services (SIM curent)</h4>
          <v-table v-if="relatedClientServices && relatedClientServices.length > 0" density="compact" class="mb-3">
            <thead>
              <tr>
                <th class="text-left">Service</th>
                <th class="text-left">Status</th>
                <th class="text-left">Expiration</th>
                <th class="text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cs in relatedClientServices" :key="cs.client_service_id">
                <td>{{ cs.service_name }}</td>
                <td>
                  <v-chip :color="getCsStatusColor(cs.cs_status)" size="x-small">
                    {{ cs.cs_status }}
                  </v-chip>
                </td>
                <td>{{ formatDate(cs.expiration_date) }}</td>
                <td>
                  <v-btn
                    variant="text"
                    size="x-small"
                    color="primary"
                    @click="goToClientService(cs.client_service_id)"
                  >
                    View
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <div v-else class="text-grey text-caption">No services found for current SIM</div>

          <!-- History Client Services (older SIMs) -->
          <div v-if="historyClientServices && historyClientServices.length > 0" class="mt-3">
            <v-btn
              variant="outlined"
              size="small"
              color="grey-darken-1"
              @click="showHistory = !showHistory"
            >
              <v-icon start size="small">{{ showHistory ? 'mdi-chevron-up' : 'mdi-history' }}</v-icon>
              History ({{ historyClientServices.length }} servicii din SIM-uri anterioare)
            </v-btn>

            <v-expand-transition>
              <div v-if="showHistory" class="mt-2">
                <v-table density="compact" class="mb-3">
                  <thead>
                    <tr>
                      <th class="text-left">Service</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">SIM ID</th>
                      <th class="text-left">SIM Created</th>
                      <th class="text-left">Expiration</th>
                      <th class="text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="cs in historyClientServices" :key="cs.client_service_id" class="text-grey">
                      <td>{{ cs.service_name }}</td>
                      <td>
                        <v-chip :color="getCsStatusColor(cs.cs_status)" size="x-small" variant="outlined">
                          {{ cs.cs_status }}
                        </v-chip>
                      </td>
                      <td>{{ cs.sim_id }}</td>
                      <td>{{ formatDateTime(cs.sim_created_at) }}</td>
                      <td>{{ formatDate(cs.expiration_date) }}</td>
                      <td>
                        <v-btn
                          variant="text"
                          size="x-small"
                          color="primary"
                          @click="goToClientService(cs.client_service_id)"
                        >
                          View
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </div>
            </v-expand-transition>
          </div>

          <!-- Admin-only Provisioning Fields -->
          <div v-if="simDetails.is_admin && simDetails.sim_status === 'not_provisioned'" class="mt-3">
            <v-divider class="my-3"></v-divider>
            <h4 class="mb-2">Provisioning Details (Admin)</h4>
            <v-row>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">Provisioning MSISDN ID:</span>
                  <span class="detail-value">{{ simDetails.provisioning_msisdn_id || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">Provisioning IMSI ID:</span>
                  <span class="detail-value">{{ simDetails.provisioning_imsi_id || '-' }}</span>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Additional Details -->
          <div class="mt-3">
            <v-divider class="my-3"></v-divider>
            <h4 class="mb-2">Additional Information</h4>
            <v-row>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">SIM ID:</span>
                  <span
                    v-if="simDetails.sim_id"
                    class="detail-value clickable-link"
                    @click="goToSimsList(simDetails.sim_id)"
                  >
                    {{ simDetails.sim_id }}
                  </span>
                  <span v-else class="detail-value">-</span>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">Status Retea:</span>
                  <v-chip v-if="simDetails.sim_status" :color="getSimStatusColor(simDetails.sim_status)" size="small">
                    {{ formatSimStatus(simDetails.sim_status) }}
                  </v-chip>
                  <span v-else class="detail-value">-</span>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">IMSI ID:</span>
                  <span class="detail-value">{{ simDetails.imsi_id || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">MSISDN ID:</span>
                  <span class="detail-value">{{ simDetails.msisdn_id || '-' }}</span>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">Created At:</span>
                  <span class="detail-value">{{ formatDateTime(simDetails.sim_created_at) }}</span>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="detail-item">
                  <span class="detail-label">SubscriberID:</span>
                  <span class="detail-value">{{ simDetails.subscriberID || '-' }}</span>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <v-alert v-if="!loading && errorMessage" type="error" density="compact">
          {{ errorMessage }}
        </v-alert>

        <v-alert v-if="updateInfoMessage" :type="updateInfoSuccess ? 'success' : 'error'" density="compact" class="mt-3">
          {{ updateInfoMessage }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn
          v-if="simDetails && simDetails.imsi_id"
          color="primary"
          variant="outlined"
          :loading="updatingInfo"
          @click="updateInfo"
        >
          <v-icon class="mr-1">mdi-refresh</v-icon>
          Update Mobile Network Info
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn variant="outlined" color="red" @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Subscriber Status Dialog -->
    <SubscriberStatusDialog
      :show="showSubscriberStatusDialog"
      :data="subscriberStatusData"
      :loading="updatingInfo"
      @close="showSubscriberStatusDialog = false"
    />
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getImsiDetails, updateImsiInfo } from "@/composables/useGetAllocatedImsisAPI";
import SubscriberStatusDialog from "@/components/dialogs/SubscriberStatusDialog.vue";
import store from "@/store";

export default defineComponent({
  name: 'ImsiDetailsPopup',
  components: {
    SubscriberStatusDialog
  },
  props: {
    simId: {
      type: [Number, String],
      default: null
    },
    imsiId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['close', 'updated'],
  data() {
    return {
      showDialog: true,
      loading: false,
      simDetails: null,
      relatedClientServices: [],
      historyClientServices: [],
      showHistory: false,
      errorMessage: null,
      updatingInfo: false,
      updateInfoMessage: null,
      updateInfoSuccess: false,
      // Subscriber Status Dialog
      showSubscriberStatusDialog: false,
      subscriberStatusData: null
    }
  },
  methods: {
    async loadDetails() {
      if (!this.simId && !this.imsiId) {
        this.errorMessage = "No SIM ID or IMSI ID provided"
        return
      }

      this.loading = true
      this.errorMessage = null

      try {
        const token = store.getters.getToken
        const response = await getImsiDetails(token, this.simId, this.imsiId)

        if (!response.error) {
          this.simDetails = response.details
          this.relatedClientServices = response.related_client_services || []
          this.historyClientServices = response.history_client_services || []
          log("Loaded SIM details:")
          log(this.simDetails)
          log("Related client services:")
          log(this.relatedClientServices)
          log("History client services:")
          log(this.historyClientServices)
        } else {
          this.errorMessage = response.message || "Error loading details"
        }
      } catch (err) {
        log("Exception loading SIM details:")
        log(err)
        this.errorMessage = "Error loading details"
      } finally {
        this.loading = false
      }
    },

    async updateInfo() {
      if (!this.simDetails || !this.simDetails.imsi_id) {
        this.updateInfoMessage = "No IMSI ID available"
        this.updateInfoSuccess = false
        return
      }

      this.updatingInfo = true
      this.updateInfoMessage = null
      this.showSubscriberStatusDialog = true
      this.subscriberStatusData = null

      try {
        const token = store.getters.getToken
        const response = await updateImsiInfo(token, this.simDetails.imsi_id)

        // Store response for the dialog
        this.subscriberStatusData = response

        if (!response.error) {
          this.updateInfoMessage = response.message || "Info updated successfully"
          this.updateInfoSuccess = true
          // Reload the details to show updated info
          await this.loadDetails()
          // Notify parent to refresh list
          this.$emit('updated')
        } else {
          this.updateInfoMessage = response.message || "Error updating info"
          this.updateInfoSuccess = false
        }
      } catch (err) {
        log("Exception updating IMSI info:")
        log(err)
        this.updateInfoMessage = "Error updating info"
        this.updateInfoSuccess = false
        this.subscriberStatusData = { error: true, message: "Error updating info" }
      } finally {
        this.updatingInfo = false
      }
    },

    closeDialog() {
      this.showDialog = false
      this.$emit('close')
    },

    onDialogClose(value) {
      if (!value) {
        this.$emit('close')
      }
    },

    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'not_provisioned': 'info'
      }
      return colors[status] || 'grey'
    },

    formatStatus(status) {
      const labels = {
        'active': 'Active',
        'suspended': 'Suspended',
        'not_provisioned': 'Not Provisioned'
      }
      return labels[status] || status
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    formatDateTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },

    isExpiringSoon(dateString) {
      if (!dateString) return false
      const expirationDate = new Date(dateString)
      const now = new Date()
      const daysUntilExpiration = (expirationDate - now) / (1000 * 60 * 60 * 24)
      return daysUntilExpiration <= 7 && daysUntilExpiration > 0
    },

    getCsStatusColor(status) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'terminated': 'error',
        'pending': 'info',
        'pending_payment': 'info'
      }
      return colors[status] || 'grey'
    },

    getSimStatusColor(status) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'not_provisioned': 'info',
        'pending_active': 'orange',
        'pending': 'orange'
      }
      return colors[status] || 'grey'
    },

    formatSimStatus(status) {
      const labels = {
        'active': 'Active',
        'suspended': 'Suspended',
        'not_provisioned': 'Not Provisioned',
        'pending_active': 'Pending Active',
        'pending': 'Pending'
      }
      return labels[status] || status || '-'
    },

    goToClientService(clientServiceId) {
      this.closeDialog()
      this.$router.push({ name: 'clientServices', query: { cs_id: clientServiceId } })
    },

    goToSimsList(simId) {
      this.closeDialog()
      this.$router.push({ name: 'sims', query: { sim_id: simId } })
    }
  },

  mounted() {
    this.loadDetails()
  }
})
</script>

<style scoped>
.detail-item {
  margin-bottom: 8px;
}

.detail-label {
  font-weight: 500;
  color: #666;
  display: block;
  font-size: 0.85rem;
}

.detail-value {
  font-size: 0.95rem;
}

.clickable-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
}

.clickable-link:hover {
  color: #1565c0;
}
</style>
